import type Phaser from "phaser";

/**
 * A chunk texture and the image that shows it, kept hidden for another chunk view of the same scene.
 */
export interface PooledChunkTexture {
  readonly texture: Phaser.Textures.DynamicTexture;
  readonly image: Phaser.GameObjects.Image;
}

/**
 * One kept entry: the texture and image, their side in pixels and when they were given.
 */
interface Kept {
  readonly entry: PooledChunkTexture;
  readonly pixels: number;
  readonly since: number;
}

/**
 * Chunk textures handed from one chunk view to the next within one scene. Making a chunk texture allocates a
 * framebuffer on the GPU, which costs a frame milliseconds; when a page swaps the views it draws a map with (a game
 * stepping onto the map beside it), the views it ends give their textures here and the views it starts paint into them
 * instead of making new ones. At most `capacity` entries are kept, each for at most `keepMs`, so what is not taken
 * soon is freed: `sweep` destroys the stale ones and is cheap when nothing is kept.
 *
 * A pool belongs to one scene, since the images it keeps sit in that scene's display list.
 *
 * @example
 * const pool = new ChunkTexturePool(12, 5000, () => performance.now());
 * const view = new ChunkView(scene, tileSize, chunkSize, animator, 12, restore, pool);
 */
export class ChunkTexturePool {
  private readonly kept: Kept[] = [];

  /**
   * @param capacity The most entries kept.
   * @param keepMs How long an entry is kept before `sweep` destroys it, in milliseconds.
   * @param now The clock `keepMs` is read on, in milliseconds.
   */
  public constructor(
    private readonly capacity: number,
    private readonly keepMs: number,
    private readonly now: () => number,
  ) {}

  /**
   * How many entries are kept.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.kept.length;
  }

  /**
   * Keeps an entry, its image hidden, while there is room.
   *
   * @param entry The texture and its image.
   * @param pixels The texture's side in pixels.
   * @returns True when kept; false when the pool is full, and the caller then destroys the entry itself.
   */
  public give(entry: PooledChunkTexture, pixels: number): boolean {
    if (this.kept.length >= this.capacity) {
      return false;
    }
    entry.image.setVisible(false);
    this.kept.push({ entry, pixels, since: this.now() });
    return true;
  }

  /**
   * Takes the entry given last of a side.
   *
   * @param pixels The side in pixels the texture must have.
   * @returns The entry, its image hidden and its texture still holding what was drawn into it; null when none of
   *   that side is kept.
   */
  public take(pixels: number): PooledChunkTexture | null {
    for (let i = this.kept.length - 1; i >= 0; i -= 1) {
      const kept = this.kept[i] as Kept;
      if (kept.pixels === pixels) {
        this.kept.splice(i, 1);
        return kept.entry;
      }
    }
    return null;
  }

  /**
   * Destroys the entries kept longer than the keeping time.
   */
  public sweep(): void {
    if (this.kept.length === 0) {
      return;
    }
    const oldest = this.now() - this.keepMs;
    let left = 0;
    for (const kept of this.kept) {
      if (kept.since < oldest) {
        kept.entry.image.destroy();
        kept.entry.texture.destroy();
      } else {
        this.kept[left] = kept;
        left += 1;
      }
    }
    this.kept.length = left;
  }

  /**
   * Destroys every entry kept.
   */
  public clear(): void {
    for (const kept of this.kept) {
      kept.entry.image.destroy();
      kept.entry.texture.destroy();
    }
    this.kept.length = 0;
  }
}
